import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi, download, parseError } from "./api";
import { SCHEMAS } from "./schemas";
import { StatusBadge } from "./ui";
import { fmtDate } from "@/lib/format";

const ExportBar = ({ kind, status }) => {
  const [range, setRange] = useState({ from: "", to: "" });
  const [busy, setBusy] = useState("");
  const label = kind === "stories" ? "Publication date" : "Event start date";
  const run = async (format) => {
    setBusy(format);
    try {
      await download(`/export/${kind}`, { format, status: status || undefined, from: range.from || undefined, to: range.to || undefined });
    } catch (e) {
      toast.error(parseError(e).message);
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="mt-4 flex flex-wrap items-end gap-3 border border-line bg-white p-4 text-sm" data-testid={`admin-export-${kind}`}>
      <div><label htmlFor="exp-from" className="block text-xs font-bold">{label} from</label><input id="exp-from" type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="field" data-testid="admin-export-from" /></div>
      <div><label htmlFor="exp-to" className="block text-xs font-bold">to</label><input id="exp-to" type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="field" data-testid="admin-export-to" /></div>
      <button type="button" disabled={Boolean(busy)} onClick={() => run("csv")} className="admin-btn-outline" data-testid="admin-export-csv">{busy === "csv" ? "Exporting…" : "Export CSV"}</button>
      <button type="button" disabled={Boolean(busy)} onClick={() => run("xlsx")} className="admin-btn-outline" data-testid="admin-export-xlsx">{busy === "xlsx" ? "Exporting…" : "Export XLSX"}</button>
      <Link to="/admin/import" className="font-bold text-brand underline" data-testid="admin-import-link">Import from spreadsheet</Link>
      <p className="w-full text-xs text-ink-muted">Exports {status ? `only ${status} records` : "all statuses, including drafts, archived and cancelled"}{range.from || range.to ? " in the selected date range" : ""}. Dates are Bengaluru time.</p>
    </div>
  );
};

export default function ContentList({ kind }) {
  const cfg = SCHEMAS[kind];
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [state, setState] = useState({ items: [], total: 0, loading: true, error: "" });
  const [tick, setTick] = useState(0);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setState((s) => ({ ...s, loading: true }));
      adminApi.get(`/${kind}`, { params: { q: q || undefined, status: status || undefined, limit: 100 } })
        .then(({ data }) => setState({ items: data.items, total: data.total, loading: false, error: "" }))
        .catch((e) => setState({ items: [], total: 0, loading: false, error: parseError(e).message }));
    }, 250);
    return () => clearTimeout(t);
  }, [kind, q, status, tick]);

  const move = async (index, dir) => {
    const ids = state.items.map((i) => i.id);
    [ids[index], ids[index + dir]] = [ids[index + dir], ids[index]];
    setMoving(true);
    try {
      await adminApi.post("/team-order", { ids });
      setTick((n) => n + 1);
    } catch (e) {
      toast.error(parseError(e).message);
    } finally {
      setMoving(false);
    }
  };
  const canReorder = kind === "team" && !q && !status;

  return (
    <div data-testid={`admin-list-${kind}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-3xl font-extrabold tracking-tight text-brand">{cfg.label}</h1>
        <Link to={`/admin/${kind}/new`} className="admin-btn inline-flex items-center gap-2" data-testid={`admin-new-${kind}`}><Plus className="h-4 w-4" aria-hidden="true" /> New {cfg.singular}</Link>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <label htmlFor="admin-search" className="sr-only">Search by {cfg.titleField}</label>
        <input id="admin-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search by ${cfg.titleField}`} className="field max-w-sm" data-testid="admin-search-input" />
        <label htmlFor="admin-status" className="sr-only">Filter by status</label>
        <select id="admin-status" value={status} onChange={(e) => setStatus(e.target.value)} className="field max-w-[12rem]" data-testid="admin-status-filter">
          <option value="">All statuses</option>
          {cfg.statuses.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <p className="mt-4 text-sm text-ink-muted" aria-live="polite" data-testid="admin-list-count">{state.loading ? "Loading…" : `${state.total} ${state.total === 1 ? cfg.singular : cfg.label.toLowerCase()}`}</p>
      {kind !== "team" && <ExportBar kind={kind} status={status} />}
      {state.error && <p role="alert" className="mt-4 font-semibold text-[#B42318]">{state.error}</p>}
      {kind === "team" && !canReorder && <p className="mt-2 text-xs text-ink-muted">Clear the search and filter to reorder profiles.</p>}

      <div className="mt-4 overflow-x-auto border border-line bg-white">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="border-b border-line bg-mist text-xs uppercase tracking-[0.1em] text-ink-muted">
            <tr>
              <th className="p-3">{kind === "team" ? "Name" : "Title"}</th><th className="p-3">Status</th>
              {cfg.columns.map(([h]) => <th key={h} className="p-3">{h}</th>)}
              <th className="p-3">Last updated</th>{canReorder && <th className="p-3">Order</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {state.items.map((d, i) => (
              <tr key={d.id} data-testid={`admin-row-${d.id}`}>
                <td className="p-3 font-bold"><Link to={`/admin/${kind}/${d.id}`} className="text-brand hover:underline" data-testid={`admin-edit-${d.slug || d.id}`}>{d[cfg.titleField]}</Link></td>
                <td className="p-3"><StatusBadge status={d.status} /></td>
                {cfg.columns.map(([h, fn]) => <td key={h} className="p-3 text-ink-muted">{fn(d)}</td>)}
                <td className="p-3 text-ink-muted">{d.updatedAt ? fmtDate(d.updatedAt) : "—"}</td>
                {canReorder && (
                  <td className="p-3">
                    <button type="button" disabled={i === 0 || moving} onClick={() => move(i, -1)} aria-label={`Move ${d.name} up`} className="p-1 disabled:opacity-30" data-testid={`admin-move-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
                    <button type="button" disabled={i === state.items.length - 1 || moving} onClick={() => move(i, 1)} aria-label={`Move ${d.name} down`} className="p-1 disabled:opacity-30" data-testid={`admin-move-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
                  </td>
                )}
              </tr>
            ))}
            {!state.loading && !state.items.length && (
              <tr><td colSpan={9} className="p-8 text-center text-ink-muted" data-testid="admin-list-empty">No {cfg.label.toLowerCase()} found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
